<template>
  <div class="compare-root">
    <div class="compare-header">
      <div>
        <div class="compare-title">Model Compare</div>
        <div class="compare-subtitle">
          {{ diffSummary.length }} different field<span v-if="diffSummary.length !== 1">s</span>
        </div>
      </div>
      <v-chip color="primary" variant="tonal" class="compare-chip">
        {{ viewModeLabel }}
      </v-chip>
    </div>

    <v-tabs v-model="activeTab" color="primary" class="compare-tabs">
      <v-tab value="summary">Diff Summary</v-tab>
      <v-tab value="side-by-side">Side by Side</v-tab>
    </v-tabs>

    <v-window v-model="activeTab" class="compare-window">
      <v-window-item value="summary">
        <div v-if="diffSummary.length === 0" class="empty-state">
          <v-icon size="42" color="success">mdi-check-circle-outline</v-icon>
          <div class="empty-title">No differences found</div>
          <div class="empty-subtitle">Both model configurations match on all tracked fields.</div>
        </div>

        <v-table v-else class="summary-table">
          <thead>
            <tr>
              <th class="summary-col-section">Section</th>
              <th class="summary-col-field">Field</th>
              <th class="summary-col-value">{{ leftTitle }}</th>
              <th class="summary-arrow-col"></th>
              <th class="summary-col-value">{{ rightTitle }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, rowIndex) in diffSummary" :key="row.key" class="summary-row summary-row-changed">
              <td
                v-if="sectionRowspan(rowIndex)"
                :rowspan="sectionRowspan(rowIndex)"
                class="summary-section-cell"
              >{{ row.section }}</td>
              <td>{{ row.label }}</td>
              <td class="summary-cell summary-cell-removed">
                <template v-if="isTextDiffRow(row)">
                  <div class="summary-diff-panel summary-diff-panel-removed">
                    <div
                      v-for="block in getNonContextBlocks(row)"
                      :key="`${row.key}-summary-left-block-${block.originalIndex}`"
                      class="summary-diff-block"
                    >
                      <div
                        v-for="(pair, pairIndex) in buildDiffLinePairs(block)"
                        :key="`${row.key}-summary-left-${block.originalIndex}-${pairIndex}`"
                        class="summary-diff-line"
                      >
                        <span class="diff-line-prefix">{{ pair.leftPrefix }}</span>
                        <span class="summary-inline-content">
                          <span
                            v-for="(segment, segmentIndex) in pair.leftSegments"
                            :key="`${row.key}-summary-left-${block.originalIndex}-${pairIndex}-${segmentIndex}`"
                            :class="['inline-diff-segment', segment.className]"
                          >
                            {{ segment.text }}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                </template>
                <div v-else :class="valueClass(row.type)">{{ row.leftDisplay }}</div>
              </td>
              <td class="summary-arrow-col">
                <div v-if="isTextDiffRow(row)" class="summary-apply-stack">
                  <div
                    v-for="block in getNonContextBlocks(row)"
                    :key="`${row.key}-summary-apply-${block.originalIndex}`"
                    class="summary-apply-slot"
                  >
                    <v-icon
                      v-if="canApplyTextBlock(block)"
                      size="18"
                      class="apply-icon"
                      @click="applySummaryTextBlock(row, block.originalIndex)"
                    >mdi-arrow-right</v-icon>
                  </div>
                </div>
                <v-icon
                  v-else-if="isFieldApplied(row.key)"
                  size="18"
                  class="reset-icon"
                  @click="resetField(row)"
                >mdi-undo</v-icon>
                <v-icon
                  v-else-if="canApplyRow(row)"
                  size="18"
                  class="apply-icon"
                  @click="applyField(row)"
                >mdi-arrow-right</v-icon>
              </td>
              <td :class="['summary-cell', isFieldApplied(row.key) ? 'summary-cell-applied' : 'summary-cell-added']">
                <template v-if="isTextDiffRow(row)">
                  <div class="summary-diff-panel summary-diff-panel-added">
                    <div
                      v-for="block in getNonContextBlocks(row)"
                      :key="`${row.key}-summary-right-block-${block.originalIndex}`"
                      class="summary-diff-block"
                    >
                      <div
                        v-for="(pair, pairIndex) in buildDiffLinePairs(block)"
                        :key="`${row.key}-summary-right-${block.originalIndex}-${pairIndex}`"
                        :class="['summary-diff-line', { 'flash-highlight': isSummaryFlashBlock(row, block.originalIndex) }]"
                      >
                        <span class="diff-line-prefix">{{ pair.rightPrefix }}</span>
                        <span class="summary-inline-content">
                          <span
                            v-for="(segment, segmentIndex) in pair.rightSegments"
                            :key="`${row.key}-summary-right-${block.originalIndex}-${pairIndex}-${segmentIndex}`"
                            :class="['inline-diff-segment', segment.className]"
                          >
                            {{ segment.text }}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                </template>
                <div v-else :class="valueClass(row.type)">{{ row.rightDisplay }}</div>
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-window-item>

      <v-window-item value="side-by-side">
        <div class="compare-grid-header">
          <div class="grid-spacer"></div>
          <div class="model-column-title model-column-title-left">{{ leftTitle }}</div>
          <div class="grid-gutter"></div>
          <div class="model-column-title model-column-title-right">{{ rightTitle }}</div>
        </div>

        <div v-for="section in sections" :key="section.key" class="section-card">
          <div class="section-title-row">
            <div class="section-title">{{ section.title }}</div>
            <v-chip
              size="small"
              variant="tonal"
              :color="sectionChangedCount(section) > 0 ? 'warning' : 'success'"
            >
              {{ sectionChangedCount(section) }} change<span v-if="sectionChangedCount(section) !== 1">s</span>
            </v-chip>
          </div>

          <div v-for="row in section.rows" :key="row.key" :class="['compare-row', { changed: row.changed }]">
            <div class="compare-label-wrap">
              <div class="compare-label">{{ row.label }}</div>
            </div>

            <template v-if="isTextDiffRow(row)">
              <div class="text-diff-stack">
                <div
                  v-for="(block, blockIndex) in getTextDiffBlocks(row)"
                  :key="`${row.key}-diff-row-${blockIndex}`"
                  class="text-diff-row"
                >
                  <div :class="['text-diff-block', textDiffBlockClass(block.type, 'left')]">
                    <div
                      v-for="(line, lineIndex) in getPanelLines(block, 'left')"
                      :key="`${row.key}-left-line-${blockIndex}-${lineIndex}`"
                      class="diff-line"
                    >
                      <span class="diff-line-prefix">{{ line.prefix }}</span>
                      <span class="diff-line-content">
                        <span
                          v-for="(segment, segmentIndex) in line.segments"
                          :key="`${row.key}-left-line-${blockIndex}-${lineIndex}-${segmentIndex}`"
                          :class="['inline-diff-segment', segment.className]"
                        >
                          {{ segment.text }}
                        </span>
                      </span>
                    </div>
                  </div>

                  <div class="compare-gutter compare-gutter-actions">
                    <div class="diff-action-slot">
                      <v-btn
                        v-if="canApplyTextBlock(block)"
                        icon
                        variant="text"
                        size="small"
                        class="apply-btn"
                        @click="applyTextBlock(row, blockIndex)"
                      >
                        <v-icon size="18">mdi-arrow-right</v-icon>
                      </v-btn>
                    </div>
                  </div>

                  <div
                    :class="[
                      'text-diff-block',
                      textDiffBlockClass(block.type, 'right'),
                      { 'flash-highlight': isSideFlashBlock(row, blockIndex) },
                    ]"
                  >
                    <div
                      v-for="(line, lineIndex) in getPanelLines(block, 'right')"
                      :key="`${row.key}-right-line-${blockIndex}-${lineIndex}`"
                      class="diff-line"
                    >
                      <span class="diff-line-prefix">{{ line.prefix }}</span>
                      <span class="diff-line-content">
                        <span
                          v-for="(segment, segmentIndex) in line.segments"
                          :key="`${row.key}-right-line-${blockIndex}-${lineIndex}-${segmentIndex}`"
                          :class="['inline-diff-segment', segment.className]"
                        >
                          {{ segment.text }}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <template v-else>
              <div class="compare-value-block">
                <div :class="['compare-value', row.type, row.changed ? 'compare-value-removed' : 'compare-value-neutral']">{{ row.leftDisplay }}</div>
              </div>
              <div class="compare-gutter compare-gutter-actions">
                <div class="diff-action-slot">
                  <v-btn
                    v-if="isFieldApplied(row.key) && !isTextDiffRow(row)"
                    icon
                    variant="text"
                    size="small"
                    class="reset-btn"
                    @click="resetField(row)"
                  >
                    <v-icon size="18">mdi-undo</v-icon>
                  </v-btn>
                  <v-btn
                    v-else-if="canApplyRow(row) && !isTextDiffRow(row)"
                    icon
                    variant="text"
                    size="small"
                    class="apply-btn"
                    @click="applyField(row)"
                  >
                    <v-icon size="18">mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
              </div>
              <div class="compare-value-block">
                <div :class="['compare-value', row.type, isFieldApplied(row.key) ? 'compare-value-applied' : row.changed ? 'compare-value-added' : 'compare-value-neutral']">{{ row.rightDisplay }}</div>
              </div>
            </template>
          </div>
        </div>
      </v-window-item>
    </v-window>
  </div>
</template>

<script>
import { diffLines, diffWordsWithSpace } from "diff";
import { buildCompareSections, buildCompareSummary } from "@/utils/gptModelCompare";

export default {
  name: "GPTModelCompare",
  props: {
    leftModel: {
      type: Object,
      required: true,
    },
    rightModel: {
      type: Object,
      required: true,
    },
    leftTitle: {
      type: String,
      default: "Left Model",
    },
    rightTitle: {
      type: String,
      default: "Right Model",
    },
    defaultView: {
      type: String,
      default: "summary",
    },
    allowApplyText: {
      type: Boolean,
      default: false,
    },
    excludedKeys: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["apply-field"],
  data() {
    return {
      activeTab: this.defaultView,
      diffBlockCache: {},
      flashTarget: null,
      flashTimer: null,
      appliedFields: {},
    };
  },
  beforeUnmount() {
    if (this.flashTimer) {
      clearTimeout(this.flashTimer);
    }
  },
  watch: {
    leftModel: {
      handler() {
        this.diffBlockCache = {};
      },
      deep: true,
    },
    rightModel: {
      handler() {
        this.diffBlockCache = {};
      },
      deep: true,
    },
  },
  computed: {
    sections() {
      return buildCompareSections(this.leftModel, this.rightModel, this.excludedKeys);
    },
    diffSummary() {
      return buildCompareSummary(this.leftModel, this.rightModel, this.excludedKeys);
    },
    sectionSpans() {
      const spans = [];
      const rows = this.diffSummary;
      let i = 0;
      while (i < rows.length) {
        let j = i + 1;
        while (j < rows.length && rows[j].section === rows[i].section) j++;
        spans.push({ start: i, count: j - i });
        i = j;
      }
      return spans;
    },
    viewModeLabel() {
      return this.activeTab === "summary" ? "Summary" : "Detailed";
    },
  },
  methods: {
    sectionRowspan(rowIndex) {
      const span = this.sectionSpans.find((s) => s.start === rowIndex);
      return span ? span.count : 0;
    },
    sectionChangedCount(section) {
      return section.rows.filter((row) => row.changed).length;
    },
    valueClass(type) {
      return ["multiline", "code", "list-text"].includes(type) ? "summary-pre" : "summary-text";
    },
    isTextDiffRow(row) {
      return ["multiline", "code", "text-diff", "list-text"].includes(row.type);
    },
    canApplyRow(row) {
      return this.allowApplyText && row.changed;
    },
    isFieldApplied(key) {
      return key in this.appliedFields;
    },
    resetField(row) {
      const stored = this.appliedFields[row.key];
      if (!stored) return;
      const { [row.key]: _, ...rest } = this.appliedFields;
      this.appliedFields = rest;
      this.scheduleApply(
        {
          key: row.key,
          value: stored.originalValue,
        },
        {
          key: row.key,
          scope: "summary",
          blockIndex: 0,
        }
      );
    },
    canApplyTextBlock(block) {
      return (
        this.allowApplyText &&
        block.type !== "context" &&
        this.normalizeBlockLines(block.leftLines) !== this.normalizeBlockLines(block.rightLines)
      );
    },
    applyField(row) {
      this.appliedFields = {
        ...this.appliedFields,
        [row.key]: { originalValue: row.rightValue },
      };
      this.scheduleApply(
        {
          key: row.key,
          value: row.leftValue,
        },
        {
          key: row.key,
          scope: "summary",
          blockIndex: 0,
        }
      );
    },
    applySummaryTextBlock(row, blockIndex) {
      const nextText = this.buildAppliedText(row, blockIndex);

      this.scheduleApply(
        {
          key: row.key,
          value: this.fromDiffText(row, nextText),
        },
        {
          key: row.key,
          scope: "summary",
          blockIndex,
        }
      );
    },
    applyTextBlock(row, blockIndex) {
      const nextText = this.buildAppliedText(row, blockIndex);

      this.scheduleApply(
        {
          key: row.key,
          value: this.fromDiffText(row, nextText),
        },
        {
          key: row.key,
          scope: "side",
          blockIndex,
        }
      );
    },
    scheduleApply(payload, flashTarget) {
      if (this.flashTimer) {
        clearTimeout(this.flashTimer);
      }

      this.flashTarget = flashTarget;
      this.flashTimer = setTimeout(() => {
        this.$emit("apply-field", payload);
        this.flashTarget = null;
        this.flashTimer = null;
      }, 300);
    },
    isSummaryFlashBlock(row, blockIndex) {
      return (
        this.flashTarget?.scope === "summary" &&
        this.flashTarget?.key === row.key &&
        this.flashTarget?.blockIndex === blockIndex
      );
    },
    isSideFlashBlock(row, blockIndex) {
      return (
        this.flashTarget?.scope === "side" &&
        this.flashTarget?.key === row.key &&
        this.flashTarget?.blockIndex === blockIndex
      );
    },
    buildAppliedText(row, blockIndex) {
      return this.getTextDiffBlocks(row)
        .flatMap((block, currentIndex) =>
          currentIndex === blockIndex ? [...block.leftLines] : [...block.rightLines]
        )
        .join("\n");
    },
    normalizeBlockLines(lines) {
      const normalized = [...lines];

      while (normalized.length > 0 && normalized[normalized.length - 1] === "") {
        normalized.pop();
      }

      return normalized.join("\n");
    },
    getTextDiffBlocks(row) {
      if (this.diffBlockCache[row.key]) {
        return this.diffBlockCache[row.key];
      }

      const leftText = this.toDiffText(row, row.leftValue);
      const rightText = this.toDiffText(row, row.rightValue);
      const parts = diffLines(leftText, rightText, { newlineIsToken: false });
      const blocks = [];

      for (let index = 0; index < parts.length; index += 1) {
        const part = parts[index];
        const nextPart = parts[index + 1];

        if (part.removed && nextPart && nextPart.added) {
          blocks.push({
            type: "replace",
            leftLines: this.toLines(part.value),
            rightLines: this.toLines(nextPart.value),
          });
          index += 1;
          continue;
        }

        if (part.removed) {
          blocks.push({
            type: "removed",
            leftLines: this.toLines(part.value),
            rightLines: [],
          });
          continue;
        }

        if (part.added) {
          blocks.push({
            type: "added",
            leftLines: [],
            rightLines: this.toLines(part.value),
          });
          continue;
        }

        blocks.push({
          type: "context",
          leftLines: this.toLines(part.value),
          rightLines: this.toLines(part.value),
        });
      }

      this.diffBlockCache[row.key] = blocks;
      return blocks;
    },
    toDiffText(row, value) {
      if (row.type === "list-text") {
        return Array.isArray(value) ? value.filter((item) => `${item ?? ""}`.trim() !== "").join("\n") : "";
      }

      return value === null || value === undefined ? "" : `${value}`;
    },
    fromDiffText(row, value) {
      if (row.type === "list-text") {
        return value
          .split("\n")
          .map((line) => line.replace(/\r/g, ""))
          .filter((line) => line.trim() !== "");
      }

      return value;
    },
    getNonContextBlocks(row) {
      return this.getTextDiffBlocks(row)
        .map((block, index) => ({ ...block, originalIndex: index }))
        .filter((block) => block.type !== "context");
    },
    buildDiffLinePairs(block) {
      const maxLength = Math.max(block.leftLines.length, block.rightLines.length, 1);

      return Array.from({ length: maxLength }, (_, index) => {
        const leftLine = block.leftLines[index] ?? "";
        const rightLine = block.rightLines[index] ?? "";
        const inlineDiff = this.buildInlineSegments(leftLine, rightLine, block.type);

        return {
          leftPrefix: this.getLinePrefix(block.type, "left"),
          rightPrefix: this.getLinePrefix(block.type, "right"),
          leftSegments: inlineDiff.left,
          rightSegments: inlineDiff.right,
        };
      });
    },
    buildInlineSegments(leftLine, rightLine, blockType) {
      if (blockType === "removed") {
        return {
          left: [{ text: leftLine || " ", className: "inline-diff-removed" }],
          right: [{ text: " ", className: "inline-diff-empty" }],
        };
      }

      if (blockType === "added") {
        return {
          left: [{ text: " ", className: "inline-diff-empty" }],
          right: [{ text: rightLine || " ", className: "inline-diff-added" }],
        };
      }

      const segments = diffWordsWithSpace(leftLine, rightLine);
      const left = [];
      const right = [];

      segments.forEach((segment) => {
        if (!segment.added) {
          left.push({
            text: segment.value || " ",
            className: segment.removed ? "inline-diff-removed" : "inline-diff-context",
          });
        }

        if (!segment.removed) {
          right.push({
            text: segment.value || " ",
            className: segment.added ? "inline-diff-added" : "inline-diff-context",
          });
        }
      });

      return {
        left: left.length ? left : [{ text: " ", className: "inline-diff-empty" }],
        right: right.length ? right : [{ text: " ", className: "inline-diff-empty" }],
      };
    },
    toLines(value) {
      if (!value) {
        return [];
      }

      const normalizedValue = value.replace(/\r/g, "");
      const lines = normalizedValue.split("\n");

      // A trailing newline terminates the last line; it is not an extra blank row.
      if (normalizedValue.endsWith("\n")) {
        lines.pop();
      }

      return lines;
    },
    getPanelLines(block, side) {
      const isLeft = side === "left";
      const lines = isLeft ? block.leftLines : block.rightLines;

      if (!lines.length) {
        return [{ prefix: " ", segments: [{ text: "", className: "inline-diff-empty" }] }];
      }

      return this.buildDiffLinePairs(block).map((pair) => ({
        prefix: isLeft ? pair.leftPrefix : pair.rightPrefix,
        segments: isLeft ? pair.leftSegments : pair.rightSegments,
      }));
    },
    getLinePrefix(type, side) {
      if (type === "context") {
        return " ";
      }

      if (type === "removed") {
        return side === "left" ? "-" : " ";
      }

      if (type === "added") {
        return side === "right" ? "+" : " ";
      }

      if (type === "replace") {
        return side === "left" ? "-" : "+";
      }

      return " ";
    },
    textDiffBlockClass(type, side) {
      if (type === "context") {
        return "diff-context";
      }

      if (type === "removed") {
        return side === "left" ? "diff-removed" : "diff-empty";
      }

      if (type === "added") {
        return side === "right" ? "diff-added" : "diff-empty";
      }

      if (type === "replace") {
        return side === "left" ? "diff-removed" : "diff-added";
      }

      return "diff-context";
    },
  },
};
</script>

<style scoped>
.compare-root {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.compare-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.compare-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1f2937;
}

.compare-subtitle {
  color: #6b7280;
  font-size: 0.9rem;
}

.compare-tabs {
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.compare-window {
  min-height: 360px;
}

.empty-state {
  min-height: 280px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: 10px;
  color: #475569;
}

.empty-title {
  font-size: 1.05rem;
  font-weight: 700;
}

.empty-subtitle {
  max-width: 480px;
}

.summary-table {
  border: 1px solid rgba(39, 52, 69, 0.14);
  border-radius: 14px;
  overflow: hidden;
}

.summary-table :deep(table) {
  table-layout: fixed;
  width: 100%;
}

.summary-table th {
  font-weight: 700;
  color: #334155;
  background: #f6f8fa;
}

.summary-col-section {
  width: 110px;
}

.summary-col-field {
  width: 110px;
}

.summary-col-value {
  overflow-wrap: break-word;
  word-break: break-word;
}

.summary-row-changed {
  background: #ffffff;
}

.summary-cell {
  vertical-align: top;
  overflow-wrap: break-word;
  word-break: break-word;
  overflow: hidden;
  max-width: 0;
}

.summary-cell-removed {
  background: #ffebe9;
  border-left: 4px solid #cf222e;
}

.summary-cell-added {
  background: #dafbe1;
  border-left: 4px solid #1a7f37;
}

.summary-cell-applied {
  background: #e8f0fe;
  border-left: 4px solid #1976d2;
}

.summary-arrow-col {
  width: 44px;
  text-align: center;
}

.summary-apply-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;
  min-height: 100%;
}

.summary-apply-slot {
  min-height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.summary-diff-panel {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-family: Consolas, "Courier New", monospace;
  font-size: 0.85rem;
}

.summary-diff-block + .summary-diff-block {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed rgba(0, 0, 0, 0.1);
}

.summary-diff-line {
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr);
  gap: 8px;
  align-items: start;
}

.summary-inline-content {
  white-space: pre-wrap;
  word-break: break-word;
}

.inline-diff-segment {
  border-radius: 3px;
  padding: 0 1px;
}

.inline-diff-context {
  background: transparent;
}

.inline-diff-removed {
  background: rgba(207, 34, 46, 0.18);
}

.inline-diff-added {
  background: rgba(26, 127, 55, 0.18);
}

.inline-diff-empty {
  color: #94a3b8;
}

.summary-text,
.summary-pre,
.compare-value,
.diff-line-content {
  white-space: pre-wrap;
  word-break: break-word;
}

.summary-pre,
.compare-value.code,
.compare-value.multiline,
.text-diff-panel {
  font-family: Consolas, "Courier New", monospace;
  font-size: 0.85rem;
}

.compare-grid-header,
.compare-row {
  display: grid;
  grid-template-columns: minmax(190px, 220px) minmax(0, 1fr) 44px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
}

.compare-grid-header {
  margin-bottom: 12px;
  color: #475569;
  font-weight: 700;
}

.model-column-title-left {
  color: #cf222e;
}

.model-column-title-right {
  color: #1a7f37;
}

.section-card {
  border: 1px solid rgba(39, 52, 69, 0.14);
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
}

.section-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.section-title {
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
}

.compare-row {
  padding: 12px 8px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
}

.compare-row:first-of-type {
  border-top: 0;
}

.compare-row.changed {
  background: rgba(255, 196, 1, 0.11);
}

.flash-highlight {
  animation: flash-apply 300ms ease-out;
}

.compare-label-wrap {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.compare-label {
  font-weight: 600;
  color: #334155;
}

.apply-btn {
  color: #0969da !important;
}

.apply-icon {
  color: #0969da !important;
  cursor: pointer;
}
.apply-icon:hover {
  color: #0550ae !important;
}

.reset-icon {
  color: #1976d2 !important;
  cursor: pointer;
  transition: color 0.15s;
}
.reset-icon:hover {
  color: #d32f2f !important;
}

.reset-btn {
  color: #1976d2 !important;
  transition: color 0.15s;
}
.reset-btn:hover {
  color: #d32f2f !important;
}

.summary-section-cell {
  vertical-align: middle;
  border-right: 1px solid #e2e8f0;
}

@keyframes flash-apply {
  0% {
    box-shadow: 0 0 0 0 rgba(9, 105, 218, 0.35);
    background-color: rgba(9, 105, 218, 0.2);
  }
  100% {
    box-shadow: 0 0 0 10px rgba(9, 105, 218, 0);
    background-color: transparent;
  }
}

.compare-value-block,
.text-diff-panel {
  min-width: 0;
}

.text-diff-stack {
  grid-column: 2 / -1;
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
}

.text-diff-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 44px minmax(0, 1fr);
  gap: 12px;
  align-items: stretch;
}

.compare-value,
.text-diff-panel,
.text-diff-block {
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(39, 52, 69, 0.14);
  min-height: 42px;
}

.text-diff-row:not(:first-child) .text-diff-block {
  margin-top: -1px;
}

.text-diff-row:not(:first-child) .text-diff-block {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.text-diff-row:not(:last-child) .text-diff-block {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.compare-value-neutral,
.diff-context {
  background: #f6f8fa;
  color: #24292f;
}

.compare-value-removed,
.diff-removed {
  background: #ffebe9;
  border-color: rgba(207, 34, 46, 0.35);
  color: #24292f;
}

.compare-value-added,
.diff-added {
  background: #dafbe1;
  border-color: rgba(26, 127, 55, 0.35);
  color: #24292f;
}

.compare-value-applied {
  background: #e8f0fe;
  border-color: rgba(25, 118, 210, 0.35);
  color: #24292f;
}

.diff-empty {
  background: #ffffff;
  border-style: dashed;
  color: #94a3b8;
}

.diff-line {
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr);
  gap: 8px;
  min-height: 20px;
}

.diff-line-prefix {
  font-weight: 700;
  text-align: center;
}

.compare-gutter,
.grid-gutter {
  min-height: 1px;
}

.compare-gutter-actions {
  display: flex;
  align-items: stretch;
  justify-content: center;
}

.diff-action-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
}

@media (max-width: 960px) {
  .compare-grid-header,
  .compare-row {
    grid-template-columns: 1fr;
  }

  .grid-spacer,
  .grid-gutter,
  .compare-gutter {
    display: none;
  }

  .model-column-title {
    font-size: 0.9rem;
  }

  .compare-label-wrap {
    align-items: center;
  }

  .text-diff-stack {
    grid-column: auto;
  }

  .text-diff-row {
    grid-template-columns: 1fr;
  }
}
</style>